/** The workspace tab strip: one tab per shell tab, close buttons, and `+` for a fresh Home tab. */
import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  BookOpen, Columns2, File, FileText, FlaskConical, FolderOpen, Home as HomeIcon,
  ListChecks, Plus, Search, User, Users, X,
} from 'lucide-react';
import { useJobWatcher } from '../contexts/JobWatcherContext';
import { runProgressView } from '../services/watchedJobs';
import { useShell } from './shellContext';
import { displayTitle, kindForPath, parseRunTabPath, tabPrefix } from './tabState';
import type { TabKind } from './tabState';

const ICON_CLASS = 'h-3.5 w-3.5 shrink-0';

function KindIcon({ kind }: { kind: TabKind }): React.ReactElement {
  switch (kind) {
    case 'home': return <HomeIcon className={ICON_CLASS} />;
    case 'search': return <Search className={ICON_CLASS} />;
    case 'drug': return <FileText className={ICON_CLASS} />;
    case 'compare': return <Columns2 className={ICON_CLASS} />;
    case 'drafts': return <FolderOpen className={ICON_CLASS} />;
    case 'research': return <FlaskConical className={ICON_CLASS} />;
    case 'cohort': return <ListChecks className={ICON_CLASS} />;
    case 'workstream':
    case 'report':
    case 'evidence': return <Users className={ICON_CLASS} />;
    case 'sba':
    case 'fda_letter': return <BookOpen className={ICON_CLASS} />;
    case 'account': return <User className={ICON_CLASS} />;
    default: return <File className={ICON_CLASS} />;
  }
}

const POPOVER_W = 300;
const POPOVER_TITLE = 'Investigator opened in a new tab';

/** 16 px progress ring. Determinate = an arc at `fraction`; otherwise a fixed arc that spins (motion-safe). */
function Ring({ fraction }: { fraction: number | null }): React.ReactElement {
  const r = 6;
  const c = 2 * Math.PI * r;
  const arc = fraction === null ? c * 0.25 : c * fraction;
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0"
      data-testid="tab-ring"
      data-fraction={fraction === null ? undefined : String(fraction)}
      data-indeterminate={fraction === null ? 'true' : undefined}
    >
      <circle cx="8" cy="8" r={r} fill="none" strokeWidth="2" className="stroke-accent-ring" />
      <circle
        cx="8" cy="8" r={r} fill="none" strokeWidth="2" strokeLinecap="round"
        className={`stroke-accent origin-center -rotate-90 ${fraction === null ? 'motion-safe:animate-spin' : ''}`}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        strokeDasharray={`${arc} ${c}`}
      />
    </svg>
  );
}

const PILL = 'bg-accent-tint text-accent-deep font-medium rounded-ctl';
const REORDER_HELP = 'Drag to reorder. Keyboard: Alt+Shift+Left/Right.';
type DropTarget = { id: string; position: 'before' | 'after' };

export const TabStrip: React.FC = () => {
  const {
    tabs, activeTabId, focusTab, closeTab, openTab, moveTab, announcement, dismissAnnouncement,
  } = useShell();
  const watcher = useJobWatcher();
  const wrapRef = useRef<HTMLDivElement>(null);
  const focusAfterMove = useRef<string | null>(null);
  const [pos, setPos] = useState({ left: 0, arrow: 22 });
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [reorderMessage, setReorderMessage] = useState('');

  const announced = announcement
    ? tabs.find((t) => t.path === announcement.path && t.id !== activeTabId)
    : undefined;
  const announcedId = announced?.id;

  useLayoutEffect(() => {
    const id = focusAfterMove.current;
    if (!id) return;
    focusAfterMove.current = null;
    wrapRef.current?.querySelector<HTMLElement>(`[data-testid="shell-tab-${id}"]`)?.focus();
  }, [tabs]);

  const clearDrag = () => {
    setDraggedId(null);
    setDropTarget(null);
  };

  const dropPosition = (event: React.DragEvent): 'before' | 'after' => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientX > rect.left + rect.width / 2 ? 'after' : 'before';
  };

  const moveByKeyboard = (event: React.KeyboardEvent, id: string) => {
    if (!event.altKey || !event.shiftKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.id === id);
    const nextIndex = index + (event.key === 'ArrowLeft' ? -1 : 1);
    const target = tabs[nextIndex];
    if (index < 0 || !target) return;
    focusAfterMove.current = id;
    moveTab(id, target.id, event.key === 'ArrowLeft' ? 'before' : 'after');
    setReorderMessage(`${tabs[index]!.title} moved to position ${nextIndex + 1} of ${tabs.length}.`);
  };

  // R3.2: anchor under the announced tab, clamped inside the wrapper. jsdom rects are 0.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !announcedId) return undefined;
    const measure = () => {
      const tabEl = wrap.querySelector(`[data-testid="shell-tab-${announcedId}"]`)?.parentElement;
      if (!tabEl) return;
      const wr = wrap.getBoundingClientRect();
      const tabLeft = tabEl.getBoundingClientRect().left - wr.left;
      const left = Math.max(0, Math.min(tabLeft, wr.width - POPOVER_W));
      setPos((p) => (p.left === left && p.arrow === tabLeft - left + 22 ? p : { left, arrow: tabLeft - left + 22 }));
    };
    measure();
    const list = wrap.querySelector('[role="tablist"]');
    list?.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    return () => {
      list?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [announcedId, tabs, activeTabId]);

  return (
    <div ref={wrapRef} className="relative flex min-w-0 flex-1 items-stretch">
      <div role="tablist" className="flex items-stretch gap-1 overflow-x-auto">
        {tabs.map((t) => {
          const active = t.id === activeTabId;
          const kind = kindForPath(t.path);
          const prefix = kind === 'home' ? null : tabPrefix(t.path);
          const run = parseRunTabPath(t.path);
          const live = run ? watcher?.running.find((j) => j.jobId === run.jobId) : undefined;
          const done = run && !live
            ? watcher?.finished.find((f) => f.jobId === run.jobId && f.outcome === 'complete')
            : undefined;
          const view = run && live
            ? runProgressView(watcher?.progress[run.jobId] ?? null, run.pinned)
            : null;
          const pill = live || done;
          const label = displayTitle(t.title, !!prefix);
          return (
            <div
              key={t.id}
              data-drop-position={dropTarget?.id === t.id ? dropTarget.position : undefined}
              className={`relative flex shrink-0 items-center border-b-2 ${
                active ? 'border-accent text-ink font-medium' : 'border-transparent text-ink-2 hover:text-ink'
              } ${dropTarget?.id === t.id && dropTarget.position === 'before'
                ? 'before:pointer-events-none before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:bg-accent'
                : ''} ${dropTarget?.id === t.id && dropTarget.position === 'after'
                ? 'after:pointer-events-none after:absolute after:inset-y-1 after:right-0 after:w-0.5 after:bg-accent'
                : ''}`}
            >
              <button
                type="button"
                role="tab"
                draggable
                aria-selected={active}
                aria-label={live ? `${t.title}, running` : t.title}
                aria-describedby="workspace-tab-reorder-help"
                aria-keyshortcuts="Alt+Shift+ArrowLeft Alt+Shift+ArrowRight"
                title={live
                  ? `Investigator · ${live.criterion} · running. Closing this tab does not stop the run. ${REORDER_HELP}`
                  : `${t.title}. ${REORDER_HELP}`}
                data-testid={`shell-tab-${t.id}`}
                onClick={() => focusTab(t.id)}
                onKeyDown={(event) => moveByKeyboard(event, t.id)}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', t.id);
                  setDraggedId(t.id);
                  setDropTarget(null);
                }}
                onDragOver={(event) => {
                  if (!draggedId || draggedId === t.id) return;
                  event.preventDefault();
                  const position = dropPosition(event);
                  setDropTarget((current) => (
                    current?.id === t.id && current.position === position ? current : { id: t.id, position }
                  ));
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggedId && draggedId !== t.id
                    && tabs.some((tab) => tab.id === draggedId)
                    && tabs.some((tab) => tab.id === t.id)) {
                    moveTab(draggedId, t.id, dropTarget?.id === t.id ? dropTarget.position : dropPosition(event));
                  }
                  clearDrag();
                }}
                onDragEnd={clearDrag}
                className={`flex max-w-[14rem] items-center gap-1.5 px-3 py-2 text-sm ${pill ? PILL : ''}`}
              >
                {live && view ? (
                  <>
                    <Ring fraction={view.fraction} />
                    <span className="font-mono text-[11px]">INV</span>
                    <span className="max-w-[170px] truncate">{live.criterion}</span>
                    {view.figure && <span className="font-mono text-[11px]">{view.figure}</span>}
                    <span className="sr-only">Running</span>
                  </>
                ) : done ? (
                  <>
                    <span className="font-mono text-[11px]">INV</span>
                    <span className="max-w-[170px] truncate">{done.criterion}</span>
                    <span className="font-mono text-[11px]">{`${done.matched ?? 0} matched`}</span>
                  </>
                ) : (
                  <>
                    {prefix
                      ? <span className="font-mono text-[11px] text-accent">{prefix}</span>
                      : kind !== 'home' && <KindIcon kind={kind} />}
                    <span className="truncate">{label}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                aria-label={`Close ${t.title}`}
                data-testid={`shell-tab-close-${t.id}`}
                onClick={() => closeTab(t.id)}
                className="mr-1 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          aria-label="New tab"
          data-testid="shell-tab-new"
          onClick={() => openTab('/')}
          className="shrink-0 rounded p-1.5 text-slate-500 hover:bg-slate-200"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <span id="workspace-tab-reorder-help" className="sr-only">{REORDER_HELP}</span>
      {reorderMessage && <span role="status" className="sr-only">{reorderMessage}</span>}
      {announcement && announced && (
        <div
          role="status"
          data-testid="shell-announce"
          className="absolute top-full z-30 mt-2 w-[300px] rounded bg-ink p-3.5 text-sm text-panel shadow-lg"
          style={{ left: pos.left }}
        >
          <span
            aria-hidden="true"
            className="absolute -top-1 h-2 w-2 rotate-45 bg-ink"
            style={{ left: pos.arrow }}
          />
          <p className="font-medium">{POPOVER_TITLE}</p>
          <p className="mt-1 text-xs text-panel/80">{announcement.body}</p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid="shell-announce-go"
              onClick={() => { focusTab(announced.id); dismissAnnouncement(); }}
              className="rounded-ctl bg-panel px-2.5 py-1 text-xs font-medium text-ink"
            >
              Go to tab
            </button>
            <button
              type="button"
              data-testid="shell-announce-dismiss"
              onClick={() => dismissAnnouncement()}
              className="text-xs text-panel/80 underline"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabStrip;
